import {
  FilterQuery,
  FilterQueryRequest
} from 'src/app/models/filter-query.model';
import { DecimalPipe, formatDate } from '@angular/common';
import {
  HttpClient,
  HttpContext,
  HttpHeaders,
  HttpParams
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import {
  NEVER,
  Observable,
  catchError,
  combineLatest,
  delay,
  finalize,
  map,
  merge,
  of,
  takeUntil,
  tap
} from 'rxjs';
import { MitigationStatusList } from 'src/app/constants/mitigation-status.const';
import { materializedRiskColumn } from 'src/app/constants/risk-column-alias.const';
import {
  RISK_RATING_SQL,
  SURVEY_RISKS_COLUMNS
} from 'src/app/constants/table-columns.const';
import { GeoJsonTypesEnum } from 'src/app/enums/geojson-types.enum';
import { MappingDataQualityTypesEnum } from 'src/app/enums/mapping-data-quality-types.enum';
import { RiskRatingsEnum } from 'src/app/enums/risk-ratings.enum';
import {
  AsyncReportJobResponse,
  AsyncReportPollResponse,
  RiskVerificationAssetsJobResponse,
  RiskVerificationAssetsPollResponse,
  SurveyExportJobResponse,
  SurveyExportPollResponse
} from 'src/app/models/async-report.model';
import { ColumnDefinition } from 'src/app/models/column-definition.model';
import {
  Dataset,
  FavoriteFilter,
  GeoJsonType,
  GeospatialRiskCounts,
  GeospatialRisksSummary,
  MappingDataQuality,
  OverallRisk,
  Plantation,
  PlantationTableFilters,
  RiskProperty,
  SurveyRisk,
  SurveyRiskUpdated,
  TotalLandArea,
  TotalMappingEffort,
  TotalPlot
} from 'src/app/models/crd-state.interface';
import { DynamicDownloadPayload } from 'src/app/models/download-risk-report-payload.model';
import { GeoJsonExportRequest } from 'src/app/models/geojson-export.model';
import {
  RISK_PREVIEW_REPORT_CONSTANTS,
  RiskReportJobResponse,
  RiskReportPollResponse,
  SystemRisk
} from 'src/app/models/risk-preview-report.model';
import {
  AddFavoriteFilterPayload,
  DeleteFavoriteFilterPayload
} from 'src/app/models/favorite-filters-payload.model';
import {
  ParsedUploadPlantation,
  PlantationValidationErrors,
  UploadPlantationPayload
} from 'src/app/models/parsed-upload-plantation.model';
import {
  SiLinkingPlantationsPayloadV1,
  SiLinkingPlantationsPayloadV2
} from 'src/app/models/si-linking-payload.model';
import { TableParams } from 'src/app/models/table-params.model';
import {
  SUPPLIER_NAME_REPORT_COLUMN,
  SUPPLIER_NAME_TABLE_COLUMN
} from 'src/app/constants/supplier-name.const';
import {
  PlotCurrentVersion,
  PlotReview,
  PlotReviewWrite
} from 'src/app/models/plot-review.model';
import { PlantationHistoryResponse } from 'src/app/summary-dashboard/plot-history/plot-history.model';
import { buildHistoryUrl } from 'src/app/summary-dashboard/plot-history/plot-history.util';
import { currentVersionFromListItem } from 'src/app/utils/plot-review.util';
import { AGRITRACE_DATA_SOURCE } from 'src/app/utils/missing-geometry.util';
import { environment } from 'src/environments/environment';
import { PagedResponse } from '../../models/paged-response.model';
import { EventStateService } from '../state-service/event-state.service';
import { UtilityService } from '../utility.service';
import {
  getRetryAfterDelay,
  QUEUED_PLANTATION_DELETE
} from 'src/app/utils/http-error.util';

/** Splits null geometry types into unmapped AgriTrace plots and failed geometry. */
const TOTAL_PLOTS_GEOMETRY_GROUP = `IF(_geo_type IS NULL AND data_source = '${AGRITRACE_DATA_SOURCE}', '${GeoJsonTypesEnum.UNMAPPED}', _geo_type) as geometry_type`;

interface SummaryQueryParams extends FilterQueryRequest {
  period: string;
  riskProperties: RiskProperty[];
}

interface MappingEffortAggregate {
  day: string;
  plantationsMapped: number;
  area_calculated_sum: number;
  data_source: string;
}

interface MappingEffortGroup {
  day: string;
  plantationsMapped: number;
  totalArea: number;
  data_source: Set<string>;
}

@Injectable({
  providedIn: 'root'
})
export class DashboardService {
  constructor(
    private readonly http: HttpClient,
    private readonly eventStateService: EventStateService,
    private readonly utilityService: UtilityService,
    private readonly decimalPipe: DecimalPipe,
    private readonly translocoService: TranslocoService
  ) {}

  private readonly noInterceptorHeaders = new HttpHeaders({
    'no-default-interceptor': '1'
  });

  getGeometryDataForExport(
    plantationCodes: string[]
  ): Observable<Plantation[]> {
    return this.http.get<Plantation[]>(
      `${
        environment.CRD_API
      }v1/plantations/download/${this.utilityService.constructMultiplePlantationsQuery(
        plantationCodes
      )}`
    );
  }

  // Async download - returns message and taskId for polling
  requestFullRiskReport(params: DynamicDownloadPayload) {
    this.eventStateService.downloadFullRiskReportLoading = true;
    const plantationCodes = params?.plantationCodes;
    const filters = structuredClone(params.filters ?? {});
    const orderBy = params?.orderBy ? `&order_by=${params.orderBy}` : '';
    const columns = {
      columns: {
        schema_version: 'v1',
        columns: [
          'plantation_code as `Plantation Code`',
          'plantation_name as `Plantation Name`',
          RISK_RATING_SQL + ' AS `Risk Rating`',
          'country as Country',
          'adm_1 as Province',
          'adm_2 as District',
          'adm_3 as `Sub District`',
          'adm_4 as Village',
          'date_created as `Date Created`',
          'data_source as `Data Source`',
          'field_team_code as `Field Team Code`',
          'survey_risks',
          'area_calculated as `Area`',
          // Only for tenants the `pmd-supplier-name-column` flag is on for:
          // the supplier lives in a `supplierName` tag that only tenants
          // sourcing plots from suppliers carry, so for everyone else this
          // would be a blank column. Reaching it needs a subquery over
          // UNNEST, which crd-api bans in caller-supplied columns except for
          // this exact string — so it must match byte for byte.
          ...(params.supplierName ? [SUPPLIER_NAME_REPORT_COLUMN] : []),
          ...params.riskColumns!
        ]
      }
    };

    if (plantationCodes?.length) {
      filters.AND = { ...filters.AND, plantation_code: plantationCodes };
    }

    return this.http.post<AsyncReportJobResponse>(
      `${environment.CRD_API}v2/plantations/download-risks/job/?date=${params.period}${orderBy}`,
      { ...columns, ...filters }
    );
  }

  downloadFullRiskReport(taskId: string) {
    return this.http.get<AsyncReportPollResponse>(
      `${environment.CRD_API}v2/plantations/download-risks/job/?task_id=${taskId}`
    );
  }

  // TODO: refactor - simplify method for dynamic downloads
  downloadMappingDataQualityReport(params: DynamicDownloadPayload) {
    this.eventStateService.downloadMappingDataQualityReportLoading = true;
    const filters = structuredClone(params.filters ?? {});
    const orderBy = params?.orderBy ? `&order_by=${params.orderBy}` : '';
    const columns = {
      columns: {
        schema_version: 'v1',
        columns: [
          'plantation_code as `Plantation Code`',
          'plantation_name as `Plantation Name`',
          'risk_self_overlap_v1_overlap_area as `Risk Plantation Overlaps Ha Overlap`',
          'risk_self_overlap_v1_overlap_area_perc as `Risk Plantation Overlaps Percentage Overlap`',
          '(select string_agg(plantation_name) from unnest(risk_self_overlap_v1_data)) as `Overlapping Plantations`',
          '(select string_agg(overlapping_plantation_code) from unnest(risk_self_overlap_v1_data)) as `Overlapping Plantation Codes`',
          ...(params.supplierName ? [SUPPLIER_NAME_REPORT_COLUMN] : [])
        ]
      }
    };
    filters.AND = { ...filters.AND, risk_self_overlap_v1_value: [true] };

    return this.http.post<Blob>(
      `${environment.CRD_API}v2/plantations/download-risks/?date=${params.period}${orderBy}`,
      { ...columns, ...filters },
      {
        responseType: 'blob' as 'json',
        observe: 'response'
      }
    );
  }

  downloadPlantationInformation(params: DynamicDownloadPayload) {
    this.eventStateService.downloadPlantationInformationLoading = true;
    const plantationCodes = params?.plantationCodes;
    const filters = structuredClone(params.filters ?? {});
    const columns = {
      columns: {
        schema_version: 'v1',
        columns: [
          'plantation_code as `Plantation Code`',
          'plantation_name as `Plantation Name`',
          'area_calculated as `Land Area`',
          'country as Country',
          'adm_1 as Province',
          'adm_2 as District',
          'adm_3 as `Sub District`',
          'adm_4 as Village',
          'date_created as `Date Created`',
          'data_source as `Data Source`',
          'field_team_code as `Field Team Code`',
          ...(params.supplierName ? [SUPPLIER_NAME_REPORT_COLUMN] : []),
          'ST_ASGEOJSON(_geo) as Geometry'
        ]
      }
    };

    if (plantationCodes?.length) {
      filters.AND = { ...filters.AND, plantation_code: plantationCodes };
    }

    return this.http.post<AsyncReportJobResponse>(
      `${environment.CRD_API}v2/plantations/download-risks/job/?date=${params.period}&plantationInformation=true`,
      { ...columns, ...filters }
    );
  }

  requestRiskVerificationAssets(plantationCodes: string[]) {
    this.eventStateService.downloadRiskVerificationAssetsLoading = true;
    return this.http.post<RiskVerificationAssetsJobResponse>(
      `${environment.CRD_API}v3/plantations/mitigation/download-assets`,
      { plantation_code: plantationCodes }
    );
  }

  downloadRiskVerificationAssets(taskId: string) {
    return this.http.get<RiskVerificationAssetsPollResponse>(
      `${environment.CRD_API}v3/plantations/mitigation/download-status/${taskId}`
    );
  }
  requestMultiLinkedPlotReport(plantationCodes: string[]) {
    this.eventStateService.downloadMultiLinkedPlotReportLoading = true;
    return this.http.post<AsyncReportJobResponse>(
      `${environment.CRD_API}v2/traceability-reports/job`,
      {
        report_type: 'multi-linked-plots',
        data: { plantation_codes: plantationCodes }
      }
    );
  }

  downloadMultiLinkedPlotReport(taskId: string) {
    return this.http.get<AsyncReportPollResponse>(
      `${environment.CRD_API}v2/traceability-reports/job/status?task_id=${taskId}`
    );
  }

  requestSurveyExport(plantationCodes: string[]) {
    this.eventStateService.downloadSurveyResponsesLoading = true;
    return this.http.post<SurveyExportJobResponse>(
      `${environment.CRD_API}v2/survey/download-csv`,
      { plantation_codes: plantationCodes }
    );
  }

  pollSurveyExportStatus(taskId: string) {
    return this.http.get<SurveyExportPollResponse>(
      `${environment.CRD_API}v2/survey/download-csv/status/${taskId}`
    );
  }

  requestGeojsonExport(body: GeoJsonExportRequest, period: string) {
    this.eventStateService.downloadGeojsonExportLoading = true;
    return this.http.post<AsyncReportJobResponse>(
      `${environment.CRD_API}v2/plantations/download-geojson/job/?date=${period}`,
      body
    );
  }

  downloadGeojsonExport(taskId: string) {
    return this.http.get<AsyncReportPollResponse>(
      `${environment.CRD_API}v2/plantations/download-geojson/job/?task_id=${taskId}`
    );
  }

  // --- Risk Preview Report (AGD-385 / AGD-386) -----------------------------

  /**
   * The full system risk catalog the user can choose from — NOT the dataset's
   * configured risks. The `compute_source=agd-geospatial` filter narrows the
   * catalog to the report-computable engine server-side (AGD-560), so survey /
   * self_overlap / bq-udfs rows and duplicate names are already excluded — the
   * picker renders the result as-is, no client-side filtering or dedupe.
   *
   * `includeCompulsory` keeps the compulsory rows (self_overlap) for the Risk
   * Configuration tab, which shows them locked. The Risk Preview Report leaves
   * it off: the report cannot compute them.
   */
  getSystemRiskCatalog(includeCompulsory = false): Observable<SystemRisk[]> {
    const headers = new HttpHeaders({
      'Accept-Language': this.utilityService.getLanguageHeaderValue(
        this.translocoService.getActiveLang()
      ),
      'skip-error-interceptor': '1'
    });
    const compulsoryParam = includeCompulsory ? '&include_compulsory=true' : '';
    return this.http.get<SystemRisk[]>(
      `${environment.CRD_API}v3/risks/system-risks/?compute_source=${RISK_PREVIEW_REPORT_CONSTANTS.COMPUTABLE_COMPUTE_SOURCE}${compulsoryParam}`,
      { headers }
    );
  }

  /**
   * Kick off the dataset-wide report. `crd` is a path param here (the
   * interceptor also appends it as a harmless `?crd=` query, ignored by the
   * path route). Returns 202 with a `report_id` to poll. Skips the global
   * error interceptor so the modal handles 400/409 with its own messages
   * (and receives the raw HttpErrorResponse rather than the unwrapped body).
   */
  requestRiskPreviewReport(
    crd: string,
    riskIds: number[]
  ): Observable<RiskReportJobResponse> {
    this.eventStateService.riskPreviewReportLoading = true;
    return this.http.post<RiskReportJobResponse>(
      `${environment.CRD_API}v3/datasets/${crd}/risk-report`,
      { risk_ids: riskIds },
      { headers: { 'skip-error-interceptor': '1' } }
    );
  }

  /**
   * Polls report status. Skips the global error interceptor so the poller can
   * treat the async row-creation 404 as transient instead of the interceptor
   * popping an error dialog on every poll during that window.
   */
  getRiskPreviewReportStatus(
    crd: string,
    reportId: string
  ): Observable<RiskReportPollResponse> {
    return this.http.get<RiskReportPollResponse>(
      `${environment.CRD_API}v3/datasets/${crd}/risk-report/${reportId}`,
      { headers: { 'skip-error-interceptor': '1' } }
    );
  }

  getDatasets(): Observable<Dataset[]> {
    return this.http.get<Dataset[]>(`${environment.CRD_API}v1/datasets/`);
  }

  getAllPlantations(params: {
    period: string;
    page: number;
    filters: FilterQuery | null;
  }): Observable<Plantation[]> {
    const columns = {
      columns: {
        schema_version: 'v1',
        columns: [
          'plantation_name',
          'plantation_code',
          'date_created',
          'adm_1',
          'adm_2',
          'country',
          'data_source'
        ]
      }
    };

    return this.http
      .post<
        PagedResponse<Plantation>
      >(`${environment.CRD_API}v2/plantations/?date=${params.period}&page=${params.page}&page_size=1000`, { ...columns, ...params.filters })
      .pipe(map((res) => res.results));
  }

  getPlantationsPaginated(params: {
    tableParams: TableParams;
    riskProperties: RiskProperty[];
    supplierName?: boolean;
  }): Observable<PagedResponse<Plantation>> {
    const { tableParams, riskProperties, supplierName } = params;
    const { period, page, pageSize, orderBy, crd, plantation_table } =
      tableParams;
    const options: { params: HttpParams; headers?: HttpHeaders } = {
      params: new HttpParams({
        fromObject: {
          date: String(period),
          page: String(page),
          page_size: pageSize
        }
      })
    };
    if (orderBy) options.params = options.params.set('order_by', orderBy);

    // Pin restore requests to their dataset instead of interceptor defaults.
    if (crd && plantation_table) {
      options.params = options.params
        .set('crd', crd)
        .set('plantation_table', plantation_table);
      options.headers = this.noInterceptorHeaders;
    }
    const riskColumn = riskProperties.map(
      (prop) =>
        `ifnull(${materializedRiskColumn(prop.name)}_value, false) as ${prop.name}_value`
    );
    const columns = {
      columns: {
        schema_version: 'v1',
        columns: [
          'plantation_code',
          'plantation_name',
          'area_calculated',
          'country',
          'adm_1',
          'adm_2',
          'adm_3',
          'adm_4',
          'date_created',
          'data_source',
          'field_team_code',
          '_docid',
          '_geo_type as geoJsonType',
          'average_monthly_yield',
          'planting_year',
          '_createdbyname',
          // TODO: update to verbatim values for compliance labels
          RISK_RATING_SQL + ' as risk_rating',
          'ifnull(has_any_risk_value, false) as has_any_risk_value',
          'mitigation_status',
          ...(supplierName ? [SUPPLIER_NAME_TABLE_COLUMN] : []),
          ...riskColumn
        ]
      }
    };
    return this.http
      .post<
        PagedResponse<Plantation>
      >(`${environment.CRD_API}v2/plantations/`, { ...columns, ...tableParams.filters }, options)
      .pipe(
        map((res) => {
          const results = res.results.map((result) => {
            const formattedDateCreated = formatDate(
              result.date_created,
              'dd/MM/yyyy, HH:mm',
              'en-US'
            );
            const formattedLandArea = this.decimalPipe.transform(
              result.area_calculated,
              '1.2-2'
            )!;
            const formattedAverageYield = this.decimalPipe.transform(
              result.average_monthly_yield,
              '1.2-2'
            );
            const geoJsonType: GeoJsonType =
              this.utilityService.getGeoJsonTypeProps(
                result.geoJsonType as GeoJsonTypesEnum,
                result.data_source
              );

            return {
              ...result,
              geoJsonType: geoJsonType,
              date_created: formattedDateCreated,
              area_calculated: formattedLandArea,
              average_monthly_yield: formattedAverageYield,
              isExpanded: false,
              checked: false,
              disabled: false,
              risk_rating: this.utilityService.getComplianceProps(
                result?.risk_rating as RiskRatingsEnum
              ),
              risks: [],
              mitigation_status_props: result.mitigation_status
                ? this.utilityService.getMitigationStatusChipProps(
                    result.mitigation_status
                  )
                : null
            };
          });
          return { ...res, results };
        }),
        tap(() => {
          this.eventStateService.isDashboardTableLoading = false;
        })
      );
  }

  getQuestionnairesPaginated(params: {
    tableParams: TableParams;
    riskProperties?: RiskProperty[];
  }): Observable<PagedResponse<Plantation>> {
    const orderBy = params.tableParams?.orderBy
      ? `&order_by=${params.tableParams.orderBy}`
      : '';
    const columns = {
      columns: {
        schema_version: 'v1',
        columns: [
          'plantation_code',
          'plantation_name',
          '_geo_type as geoJsonType',
          '_docid',
          'survey_risk_calculated_at',
          'data_source',
          'survey_risks',
          'country',
          RISK_RATING_SQL + ' as risk_rating',
          'mitigation_status'
        ]
      }
    };

    return this.http
      .post<
        PagedResponse<Plantation>
      >(`${environment.CRD_API}v2/plantations/?date=${params.tableParams.period}&page=${params.tableParams.page}&page_size=${params.tableParams.pageSize}${orderBy}`, { ...columns, ...params.tableParams.filters })
      .pipe(
        map((response) => {
          const pagedQuestionnaireList = response;

          if (pagedQuestionnaireList.results?.length) {
            pagedQuestionnaireList.results = pagedQuestionnaireList.results.map(
              (result) => {
                // Clone the existing result into updatedResult
                let updatedResult: Plantation = { ...result };

                const surveyColumns = SURVEY_RISKS_COLUMNS;
                const survey_risks_updated: SurveyRiskUpdated = {};

                let overAllSurveyMaxRiskScoreRating = -Infinity;
                let overAllSurveyMaxRiskRating: SurveyRisk | null = null;

                surveyColumns.forEach((column) => {
                  // Find the survey risk with the highest risk_score for the current column.field
                  const maxRisk = (result.survey_risks || [])
                    .filter((r: SurveyRisk) => `${r.category}` === column.field) // Filter risks that match the column field
                    .reduce(
                      (max: SurveyRisk, current: SurveyRisk) =>
                        current.risk_score > max.risk_score ? current : max,
                      { category: '', risk_score: -Infinity } as SurveyRisk // Initialize max with an invalid SurveyRisk
                    );

                  // If no risk was found (i.e., if no valid category matches), set it to undefined
                  survey_risks_updated[column.field] =
                    maxRisk && maxRisk.risk_score !== -Infinity
                      ? maxRisk
                      : null;

                  // Update globalMaxRiskScore if the current column has a higher risk score
                  if (maxRisk.risk_score > overAllSurveyMaxRiskScoreRating) {
                    overAllSurveyMaxRiskScoreRating = maxRisk.risk_score;
                    overAllSurveyMaxRiskRating = maxRisk;
                  }

                  // Set color based on the risk score (assuming getColorForScore is defined elsewhere)
                  survey_risks_updated[`${column.field}_color`] =
                    this.utilityService.getRiskScoreProps(
                      survey_risks_updated[column.field]
                    ).color;
                });

                // Check if overAllSurveyMaxRiskScoreRating is -Infinity, if so, set it to null
                const normalizedMaxRiskScoreRating =
                  overAllSurveyMaxRiskScoreRating === -Infinity
                    ? null
                    : overAllSurveyMaxRiskScoreRating;

                // Get the risk score properties only once
                const riskScoreProps = this.utilityService.getRiskScoreProps({
                  risk_score: normalizedMaxRiskScoreRating
                });

                // Merge updated survey risks into the result
                updatedResult = {
                  ...updatedResult,
                  survey_risks_updated,
                  overAllSurveyMaxRiskRatingProps: {
                    overAllSurveyMaxRiskScoreRating:
                      normalizedMaxRiskScoreRating,
                    overAllSurveyMaxRiskRating,
                    overAllSurveyMaxRiskRatingLabel: riskScoreProps.label,
                    overAllSurveyMaxRiskRatingColor: riskScoreProps.color
                  },
                  risk_rating: this.utilityService.getComplianceProps(
                    result?.risk_rating as RiskRatingsEnum
                  ),
                  mitigation_status_props: result.mitigation_status
                    ? this.utilityService.getMitigationStatusChipProps(
                        result.mitigation_status
                      )
                    : null
                };

                const geoJsonType: GeoJsonType =
                  this.utilityService.getGeoJsonTypeProps(
                    result.geoJsonType as GeoJsonTypesEnum,
                    result.data_source
                  );
                updatedResult.geoJsonType = geoJsonType;

                return updatedResult;
              }
            );
          }
          return pagedQuestionnaireList;
        }),
        tap(() => {
          this.eventStateService.isDashboardTableLoading = false;
        })
      );
  }

  getPlantationListFilters(filter: {
    datasetName?: string;
    date: string;
  }): Observable<PlantationTableFilters> {
    let params = new HttpParams();

    // Conditionally add parameters if they are present
    if (filter.datasetName) {
      params = params.set('crd', filter.datasetName);
    }

    if (filter.date) {
      params = params.set('date', filter.date);
    }

    return this.http
      .get<
        Record<string, (string | null)[]>
      >(`${environment.CRD_API}v2/plantations/filters/`, { params })
      .pipe(
        map((filters) => {
          const mitigationStatusList = [
            // TODO: uncomment when BE handles null mitigation_status filter
            // null,
            ...MitigationStatusList.map((status) =>
              this.translocoService.translate(status)
            )
          ];
          return {
            ...filters,
            risk_rating: [
              ...[
                this.translocoService.translate('DASHBOARD.COMPLIANT'),
                this.translocoService.translate('DASHBOARD.NON_COMPLIANT'),
                this.translocoService.translate('DASHBOARD.NO_DATA'),
                this.translocoService.translate('DASHBOARD.PARTIAL_DATA')
              ]
            ],
            mitigation_status: [...mitigationStatusList]
          };
        }),
        map((filters) => this.transformFilterData(filters)),
        tap(() => (this.eventStateService.isPlantationFilterLoading = false))
      );
  }

  transformFilterData(
    data: Record<string, (string | null)[]>
  ): PlantationTableFilters {
    const transformed: PlantationTableFilters = {};
    for (const [key, items] of Object.entries(data)) {
      if (data.hasOwnProperty(key)) {
        transformed[key] = items.map((item) => {
          const geojsonProps = this.utilityService.getGeoJsonTypeProps(item);

          // Options carry no data_source, so the null option stays "Invalid
          // Geometry" and also matches unmapped AgriTrace plots.
          const value =
            key === 'risk_rating'
              ? this.utilityService.getRiskRatingFilterValue(item)
              : key === 'mitigation_status'
                ? this.utilityService.getMitigationStatusFilterValue(item)
                : key === '_geo_type' && !item
                  ? 'Invalid Geometry'
                  : item;

          const filterObject = {
            label: key === '_geo_type' ? geojsonProps.label : item,
            value: value,
            icon: key === '_geo_type' ? geojsonProps.icon : null,
            type: key === '_geo_type' ? 'geojson' : null
          };

          return filterObject;
        });
      }
    }

    if (transformed['_geo_type']) {
      transformed['geometry_type'] = transformed['_geo_type'];
    }

    if (transformed['risk_rating']) {
      transformed['has_any_risk_value'] = transformed['risk_rating'];
    }

    return transformed;
  }

  processRiskRatingKeyPayloadFilter(
    valueArray: string[]
  ): Pick<Required<FilterQuery>, 'AND'> {
    return { AND: { [RISK_RATING_SQL]: valueArray } };
  }

  /**
   * summary chart calls
   */

  handleApiErrorForSummary<T>(observable: Observable<T>) {
    return observable.pipe(catchError(() => of<[]>([])));
  }

  getTotalSummary(params: SummaryQueryParams) {
    // Self-overlap is optional; only request its summary when configured.
    const mappingDataQuality$ = params.riskProperties.some(
      (risk) => risk.name === 'risk_self_overlap_v1'
    )
      ? this.getMappingDataQuality(params)
      : of<MappingDataQuality[]>([]);

    return combineLatest([
      this.handleApiErrorForSummary(this.getTotalLandAreas(params)),
      this.handleApiErrorForSummary(this.getTotalPlots(params)),
      this.handleApiErrorForSummary(this.getTotalMappingEfforts(params)),
      this.handleApiErrorForSummary(this.getOverallRisks(params)),
      this.handleApiErrorForSummary(mappingDataQuality$),
      this.handleApiErrorForSummary(this.getGeospatialRisksSummary(params))
    ]).pipe(
      finalize(() => {
        this.eventStateService.isStatisticsUpdating = false;
        this.eventStateService.isTotalSummaryLoading = false;
      })
    );
  }

  getTotalLandAreas(params: FilterQueryRequest): Observable<TotalLandArea[]> {
    const columns = {
      columns: {
        schema_version: 'v1',
        columns: [
          'country',
          'ROUND(SUM(area_calculated), 6) AS area',
          'COUNT(1) as totalCount',
          'round(sum(area_calculated)*1.2, 2) as totalYield '
        ]
      }
    };
    return this.http.post<TotalLandArea[]>(
      `${environment.CRD_API}v2/statistics/land-area/total/`,
      { ...columns, ...params.filters }
    );
  }

  getTotalPlots(params: FilterQueryRequest): Observable<TotalPlot[]> {
    const payload = {
      aggregates: [
        {
          key: '1',
          function: 'count',
          alias: 'count'
        }
      ],
      GROUP_BY: [TOTAL_PLOTS_GEOMETRY_GROUP]
    };
    return this.http
      .post<TotalPlot[]>(`${environment.CRD_API}v2/statistics/dynamic/stats/`, {
        ...payload,
        ...params.filters
      })
      .pipe(
        map((res) => {
          res.forEach((plot) => {
            const props = this.utilityService.getGeoJsonTypeProps(
              plot.geometry_type as GeoJsonTypesEnum
            );
            plot.label = props.label;
            plot.icon = props.icon;
          });
          return res;
        })
      );
  }

  getTotalMappingEfforts(
    params: FilterQueryRequest
  ): Observable<TotalMappingEffort[]> {
    const payload = {
      aggregates: [
        {
          key: '1',
          function: 'count',
          alias: 'plantationsMapped'
        },
        {
          key: 'area_calculated',
          function: 'SUM'
        }
      ],
      GROUP_BY: ['data_source', 'date_trunc(date_created, DAY) as day']
    };
    return this.http
      .post<MappingEffortAggregate[]>(
        `${environment.CRD_API}v2/statistics/dynamic/stats/?order_by=day`,
        {
          ...payload,
          ...params.filters
        }
      )
      .pipe(
        map((res) => {
          const groupedData = res.reduce<Record<string, MappingEffortGroup>>(
            (acc, curr) => {
              const day = curr.day;
              if (!acc[day]) {
                acc[day] = {
                  day: day,
                  plantationsMapped: 0,
                  totalArea: 0,
                  data_source: new Set<string>()
                };
              }
              acc[day].plantationsMapped += curr.plantationsMapped;
              acc[day].totalArea += curr.area_calculated_sum;
              if (curr.data_source) {
                acc[day].data_source.add(curr.data_source);
              }
              return acc;
            },
            {}
          );

          const result = Object.values(groupedData).map((entry) => ({
            day: entry.day,
            plantationsMapped: entry.plantationsMapped,
            totalArea: entry.totalArea,
            data_source: Array.from(entry.data_source).join(', ')
          }));

          return result;
        })
      );
  }

  getOverallRisks(params: FilterQueryRequest) {
    const payload = {
      aggregates: [
        {
          key: '1',
          function: 'count',
          alias: 'riskRatingValue'
        }
      ],
      GROUP_BY: [RISK_RATING_SQL + ' as riskRating']
    };

    return this.http
      .post<
        OverallRisk[]
      >(`${environment.CRD_API}v2/statistics/dynamic/stats/`, { ...payload, ...params.filters })
      .pipe(
        map((overallRisks) => {
          overallRisks.forEach((risk) => {
            const props = this.utilityService.getOverallRisksProps(
              risk.riskRating as RiskRatingsEnum
            );
            risk.label = props.label;
            risk.icon = props.icon;
          });
          return overallRisks;
        })
      );
  }

  getMappingDataQuality(
    params: FilterQueryRequest
  ): Observable<MappingDataQuality[]> {
    const fixedPayload = {
      aggregates: [
        {
          key: '1',
          function: 'count',
          alias: 'overlapDistribution'
        }
      ],
      GROUP_BY: [
        "IF(risk_self_overlap_v1_overlap_area_perc = 100, 'Full Overlap', IF(IFNULL(risk_self_overlap_v1_overlap_area_perc, -1) > 0, 'Partial Overlap', 'No Overlap')) as overlaps"
      ],
      OR: {
        'ifnull(risk_self_overlap_v1_value, false)': [true, false]
      }
    };

    const finalPayload = {
      ...fixedPayload,
      ...params.filters,
      OR: {
        ...fixedPayload.OR,
        ...(params.filters?.OR || {})
      }
    };

    return this.http
      .post<
        MappingDataQuality[]
      >(`${environment.CRD_API}v2/statistics/dynamic/stats/`, finalPayload)
      .pipe(
        map((mappingDataQuality) => {
          mappingDataQuality.forEach((data) => {
            switch (data.overlaps.toLowerCase()) {
              case MappingDataQualityTypesEnum.FULL_OVERLAP:
                data.color = 'var(--p-red-500)';
                data.label = this.translocoService.translate(
                  'MAPPING_QUALITY.FULL_OVERLAP'
                );
                break;
              case MappingDataQualityTypesEnum.PARTIAL_OVERLAP:
                data.color = 'var(--p-orange-500)';
                data.label = this.translocoService.translate(
                  'MAPPING_QUALITY.PARTIAL_OVERLAP'
                );
                break;
              case MappingDataQualityTypesEnum.NO_OVERLAP:
                data.color = 'var(--p-primary-color)';
                data.label = this.translocoService.translate(
                  'MAPPING_QUALITY.NO_OVERLAP'
                );
                break;
            }
          });
          return mappingDataQuality;
        })
      );
  }

  getGeospatialRisksSummary(params: SummaryQueryParams) {
    const riskColumn = params.riskProperties.map(
      (prop) =>
        `countif(ifnull(${materializedRiskColumn(prop.name)}_value, false) = true) as ${prop.name}`
    );

    riskColumn.push(
      'countif(ifnull(has_any_risk_value, false) = false) as no_risk'
    );
    const columns = {
      columns: {
        schema_version: 'v1',
        columns: [
          'count(1) as totalFarms',
          ...(riskColumn?.length ? riskColumn : [])
        ]
      }
    };

    return this.http
      .post<
        PagedResponse<GeospatialRiskCounts>
      >(`${environment.CRD_API}v2/plantations/?date=${params.period}&order_by=totalFarms`, { ...columns, ...params.filters })
      .pipe(
        map((res) => {
          return this.mapGeospatialRisksToRisksSummary(
            res.results,
            params.riskProperties
          )[0];
        })
      );
  }

  mapGeospatialRisksToRisksSummary(
    summary: GeospatialRiskCounts[],
    riskProperties: RiskProperty[]
  ) {
    return summary.map((result) => {
      const mappedSummary: GeospatialRisksSummary = { ...result };

      Object.entries(result).forEach(([key, value]) => {
        if (key.startsWith('risk_')) {
          // Handle general risk properties
          const metadata = riskProperties.find((item) => item.name === key);

          if (metadata) {
            mappedSummary[key] = {
              value,
              display_name: metadata.display_name,
              color: metadata.color,
              type: metadata.type,
              is_default: metadata.is_default,
              description: metadata.description
            };
          }
        } else if (key === 'no_risk') {
          mappedSummary[key] = {
            value,
            display_name: this.translocoService.translate(
              'RISK_TYPES.NO_RISKS'
            ),
            type: 'no_risk'
          };
        }
      });

      return mappedSummary;
    });
  }

  /**
   * end summary chart calls
   */

  getRiskProperties(): Observable<RiskProperty[]> {
    const headers = new HttpHeaders({
      'Accept-Language': this.utilityService.getLanguageHeaderValue(
        this.translocoService.getActiveLang()
      )
    });

    // Only the config resolver returns compute_source, which the tile-id
    // resolver needs to pick risk_function over name.
    return this.http
      .get<RiskProperty[]>(`${environment.CRD_API}v3/datasets/risks/`, {
        headers,
        params: new HttpParams().set('risk_resolver', 'config')
      })
      .pipe(map((risks) => risks.filter((risk) => risk.type !== 'survey')));
  }

  getUploadMappingColumns() {
    const headers = new HttpHeaders({
      'Accept-Language': this.utilityService.getLanguageHeaderValue(
        this.translocoService.getActiveLang()
      )
    });
    return this.http
      .get<{
        columns: { column: string; display: string }[];
      }>(`${environment.CRD_API}v2/file-upload/columns/`, { headers })
      .pipe(
        delay(2000),
        // TODO: update type
        map((res) => {
          this.eventStateService.matchColumnsLoading = false;
          const mappedColumns: ColumnDefinition[] = [
            ...res.columns.map((data, index) => ({
              id: ++index,
              field: data.column,
              displayName: data.display,
              editable: true,
              style: `width: ${this.getColumnWidth(data.column)}`
            }))
          ];
          mappedColumns.push({
            id: 6,
            field: 'error',
            displayName: 'Error',
            hidden: true,
            style: 'width: 30rem;',
            sortable: true
          });
          mappedColumns.push({
            id: 7,
            field: 'actions',
            displayName: 'Actions',
            style: 'width: 10rem;'
          });

          return mappedColumns;
        })
      );
  }

  validatePlantationData(
    dataset: string,
    parsedData: ParsedUploadPlantation[]
  ) {
    return this.http
      .post<PlantationValidationErrors>(
        `${environment.CRD_API}v1/plantation-data/validate/`,
        {
          dataset: dataset,
          plantation_data: parsedData
        }
      )
      .pipe(
        tap(() => {
          this.eventStateService.validationLoading = false;
        })
      );
  }

  uploadPlantationData(payload: UploadPlantationPayload) {
    return this.http
      .post(`${environment.CRD_API}v2/plantation-data/upload/`, {
        dataset: payload.dataset,
        filename: payload.filename,
        plantation_data: payload.data
      })
      .subscribe(() => {
        this.eventStateService.closePlantationUploadModal = true;
        this.eventStateService.isImportInProgress = false;
        this.utilityService.emitToast({
          isSuccess: true,
          message: this.translocoService.translate('TOAST.FILE_IS_PROCESSING')
        });
      });
  }

  deletePlantations(
    plantationCodes: string[],
    deleteModal?: DynamicDialogRef,
    cancel$: Observable<void> = NEVER,
    context = new HttpContext()
  ) {
    this.eventStateService.isBulkDeletePlantationLoading = true;
    return this.http
      .delete(`${environment.CRD_API}v3/plantations/`, {
        body: { plantation_code: plantationCodes },
        context
      })
      .pipe(
        takeUntil(merge(cancel$, deleteModal?.onClose ?? NEVER)),
        finalize(() => {
          this.eventStateService.isBulkDeletePlantationLoading = false;
          this.eventStateService.isDeletePlantationLoading = false;
          deleteModal?.close();
        }),
        tap({
          error: (error: unknown) => {
            if (
              !context.get(QUEUED_PLANTATION_DELETE) ||
              getRetryAfterDelay(error) === undefined
            ) {
              this.utilityService.emitErrorToast(error);
            }
          },
          next: () => {
            this.eventStateService.refreshTable = true;

            const key =
              plantationCodes.length === 1
                ? 'MESSAGES.PLANTATION_DELETED_SUCCESSFULLY'
                : 'MESSAGES.BULK_PLANTATION_DELETED_SUCCESSFULLY';

            this.utilityService.emitToast({
              isSuccess: true,
              message: this.translocoService.translate(key, {
                value: plantationCodes.length
              })
            });
          }
        })
      );
  }

  addFavoriteFilter(payload: AddFavoriteFilterPayload) {
    return this.http.post(
      `${environment.CRD_API}v2/filters/favorites/?dataset=${payload.dataset}`,
      payload
    );
  }

  deleteFavoriteFilter(payload: DeleteFavoriteFilterPayload) {
    const queryParams =
      `?filter_id=${payload.filter_id}` + `&dataset=${payload.dataset}`;
    return this.http.delete(
      `${environment.CRD_API}v2/filters/favorites/${queryParams}`
    );
  }

  getFavoritePlantationFilters(): Observable<FavoriteFilter[]> {
    return this.http.get<FavoriteFilter[]>(
      `${environment.CRD_API}v2/filters/favorites/`
    );
  }

  getPlantationHistory(
    plantationCode: string
  ): Observable<PlantationHistoryResponse> {
    return this.http.get<PlantationHistoryResponse>(
      buildHistoryUrl(plantationCode),
      { headers: { 'skip-error-interceptor': '1' } }
    );
  }

  getPlantationCurrentVersions(
    plantationCodes: string[]
  ): Observable<PlotCurrentVersion[]> {
    if (plantationCodes.length === 0) {
      return of([]);
    }

    let params = new HttpParams();
    for (const code of plantationCodes) {
      params = params.append('plantation_code', code);
    }

    return this.http
      .get<{
        results: PlotCurrentVersion[];
      }>(`${environment.CRD_API}v2/plantations/history`, {
        params,
        headers: { 'skip-error-interceptor': '1' }
      })
      .pipe(
        map((res) =>
          (res.results ?? []).map((row) => ({
            plantation_code: row.plantation_code,
            version: currentVersionFromListItem(row.version)
          }))
        )
      );
  }

  getPlantationReviews(): Observable<PlotReview[]> {
    return this.http
      .get<{
        results: PlotReview[];
      }>(`${environment.CRD_API}v2/plantations/reviews`, {
        headers: { 'skip-error-interceptor': '1' }
      })
      .pipe(map((res) => res.results ?? []));
  }

  putPlantationReview(payload: PlotReviewWrite): Observable<PlotReview> {
    return this.http.put<PlotReview>(
      `${environment.CRD_API}v2/plantations/review`,
      payload,
      { headers: { 'skip-error-interceptor': '1' } }
    );
  }

  deletePlantationReview(id: number): Observable<void> {
    return this.http.delete<void>(
      `${environment.CRD_API}v2/plantations/reviews/${id}`,
      {
        headers: {
          'no-default-interceptor': 'true',
          'skip-error-interceptor': '1'
        }
      }
    );
  }

  linkPlantationToSi<
    T extends SiLinkingPlantationsPayloadV1 | SiLinkingPlantationsPayloadV2
  >(payload: T, version: 'v1' | 'v2' = 'v1'): Observable<any> {
    const endpointSuffix = version === 'v2' ? '-v2' : '';
    return this.http.post(
      `${environment.TRACEABILITY_API}si/link-plantations-to-si${endpointSuffix}/`,
      payload
    );
  }

  private getColumnWidth(field: string) {
    if (field === 'geometry') return '30rem';
    return '12rem';
  }

  fetchPlantationsBySi(siDetailId: string): Observable<Plantation[]> {
    return this.http.get<Plantation[]>(
      `${environment.TRACEABILITY_API_v3}si/get-plantations-by-si/?sidetail_id=${Number(siDetailId)}&withRisks=true`,
      { headers: this.noInterceptorHeaders }
    );
  }
}
